import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const Privacy = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
        <div className="absolute inset-0 bg-[url('/images/hero-brand-image.png')] bg-cover bg-center opacity-5" />
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-secondary via-primary to-accent bg-clip-text text-transparent leading-tight">
              Privacy Policy
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Your privacy and data security are our top priorities
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            
            <Card>
              <CardHeader>
                <CardTitle>Introduction</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  At Tiny Tax Lady, we are committed to protecting your privacy and safeguarding your personal and financial information. This Privacy Policy explains how we collect, use, store, and protect your data when you use our tax preparation services.
                </p>
                <p>
                  <span className="font-semibold">Effective Date:</span> January 2025
                </p>
                <p>
                  By engaging our services, you consent to the practices described in this Privacy Policy. Please read it carefully.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Information We Collect</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  To prepare your tax returns accurately and comply with federal and state requirements, we collect the following types of information:
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Personal Identification Information</h4>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Full legal name, Social Security Number (SSN), and date of birth</li>
                      <li>Address, phone number, and email address</li>
                      <li>Spouse and dependent information (names, SSNs, dates of birth)</li>
                      <li>Driver's license or government-issued ID (for identity verification)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Financial Information</h4>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>W-2s, 1099s, and other income statements</li>
                      <li>Bank account and routing numbers (for direct deposit or payment)</li>
                      <li>Investment income, capital gains/losses, and dividend information</li>
                      <li>Business income and expenses (for sole proprietors)</li>
                      <li>Deduction records (mortgage interest, charitable contributions, medical expenses, etc.)</li>
                      <li>Prior year tax returns</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Technical and Usage Information</h4>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>IP address, browser type, and device information</li>
                      <li>Website usage data (pages visited, time spent, etc.)</li>
                      <li>Cookies and similar tracking technologies for session management</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How We Use Your Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We use your information solely for the following purposes:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><span className="font-semibold">Tax Preparation and Filing:</span> To prepare accurate federal and state income tax returns and submit them electronically to the IRS and state agencies</li>
                  <li><span className="font-semibold">Communication:</span> To contact you about your return, answer questions, provide status updates, and send appointment reminders</li>
                  <li><span className="font-semibold">IRS and State Correspondence:</span> To respond to notices, audits, or inquiries from tax authorities on your behalf (with your authorization)</li>
                  <li><span className="font-semibold">Service Improvement:</span> To enhance our services, website functionality, and client experience</li>
                  <li><span className="font-semibold">Legal Compliance:</span> To comply with applicable tax laws, regulations, and professional standards</li>
                  <li><span className="font-semibold">Record Keeping:</span> To maintain required records in accordance with IRS and state retention requirements</li>
                </ul>
                <p className="font-semibold text-foreground">
                  We will NEVER use your information for marketing purposes without your explicit consent.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Information Sharing and Disclosure</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We do not sell, rent, or trade your personal information to third parties. We may share your information only in the following limited circumstances:
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Tax Authorities (Required by Law)</h4>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>IRS (Internal Revenue Service)</li>
                      <li>State revenue departments</li>
                      <li>We are legally required to file your tax returns with these agencies</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Service Providers</h4>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Backend database and storage providers (encrypted and secure)</li>
                      <li>Email service providers (AWS SES for appointment reminders and communication)</li>
                      <li>Tax software providers for e-filing</li>
                      <li>All service providers are bound by strict confidentiality agreements</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Legal Requirements</h4>
                    <p>
                      We may disclose information if required by law, court order, subpoena, or to protect our legal rights.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground mb-2">With Your Consent</h4>
                    <p>
                      We may share information with third parties (e.g., lenders, financial advisors) only with your explicit written authorization.
                    </p>
                  </div>
                </div>

                <p className="font-semibold text-foreground pt-4">
                  We will NEVER sell your data to marketers, data brokers, or any third party for commercial purposes.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Security</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We implement industry-standard security measures to protect your sensitive information:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><span className="font-semibold">Encryption:</span> All data transmitted to and from our website is encrypted using TLS/SSL protocols (the same technology used by banks)</li>
                  <li><span className="font-semibold">Secure Storage:</span> Documents and data are stored in password-protected, encrypted databases with restricted access</li>
                  <li><span className="font-semibold">Access Controls:</span> Only authorized personnel have access to client information, and access is logged and monitored</li>
                  <li><span className="font-semibold">Physical Security:</span> Paper documents are stored in locked, secure locations</li>
                  <li><span className="font-semibold">Regular Security Updates:</span> We keep our systems up to date with the latest security patches</li>
                </ul>
                <p>
                  While we take every reasonable precaution, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your information using best practices.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Retention</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We retain your tax documents and information for a minimum of <span className="font-semibold">7 years</span> as required by IRS regulations and professional standards. This allows us to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Respond to IRS audits or inquiries</li>
                  <li>Prepare amended returns if needed</li>
                  <li>Comply with legal and regulatory requirements</li>
                </ul>
                <p>
                  After the retention period, we securely destroy records by:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Shredding paper documents</li>
                  <li>Permanently deleting electronic files</li>
                  <li>Overwriting data on storage devices</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Your Privacy Rights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  You have the following rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><span className="font-semibold">Access:</span> You may request a copy of the personal information we have on file</li>
                  <li><span className="font-semibold">Correction:</span> You may request corrections to inaccurate information</li>
                  <li><span className="font-semibold">Deletion:</span> You may request deletion of your information, subject to legal retention requirements (we must keep tax records for at least 7 years)</li>
                  <li><span className="font-semibold">Opt-Out:</span> You may opt out of non-essential communications (e.g., newsletters) but will still receive service-related messages</li>
                </ul>
                <p>
                  To exercise any of these rights, please contact us using the information provided at the end of this policy.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cookies and Tracking Technologies</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Our website uses cookies and similar technologies to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Maintain your session while using our client portal</li>
                  <li>Remember your preferences and settings</li>
                  <li>Analyze website traffic and usage patterns (anonymized data)</li>
                  <li>Improve website performance and user experience</li>
                </ul>
                <p>
                  You can control cookies through your browser settings. Disabling cookies may limit some website functionality.
                </p>
                <p>
                  We do NOT use cookies for advertising or tracking across other websites.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Third-Party Services</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We use the following third-party services to operate our business:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><span className="font-semibold">Backend Services:</span> Secure cloud database and storage (encrypted and GDPR-compliant)</li>
                  <li><span className="font-semibold">Email Services:</span> AWS SES for transactional emails (appointment reminders, notifications)</li>
                  <li><span className="font-semibold">Tax Software:</span> IRS-approved e-filing software</li>
                </ul>
                <p>
                  All third-party providers are carefully vetted and required to maintain strict data security and privacy standards. They may only use your information to provide services to us and are prohibited from using it for their own purposes.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Children's Privacy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from minors without parental consent. If you are under 18, please do not submit any information through our website or services.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>California Privacy Rights (CCPA)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Right to know what personal information we collect and how it is used</li>
                  <li>Right to request deletion of your personal information (subject to legal retention requirements)</li>
                  <li>Right to opt out of the sale of personal information (we do NOT sell your information)</li>
                  <li>Right to non-discrimination for exercising your privacy rights</li>
                </ul>
                <p>
                  To exercise your CCPA rights, please contact us using the information below.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Changes to This Privacy Policy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make changes, we will:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Post the updated policy on our website with a new "Effective Date"</li>
                  <li>Notify you by email if changes are significant</li>
                </ul>
                <p>
                  Your continued use of our services after changes are posted constitutes acceptance of the updated Privacy Policy.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contact Us</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us:
                </p>
                <div className="space-y-2 mt-4">
                  <p className="font-semibold">Tiny Tax Lady</p>
                  <p>Diane Hardesty</p>
                  <p>9330 Gillette St.</p>
                  <p>Lenexa, KS 66215</p>
                  <p>Phone: <a href="tel:913-406-7852" className="text-secondary hover:underline">913-406-7852</a></p>
                  <p>Fax: 913-271-4416</p>
                  <p>Email: <a href="mailto:Diane@TinyTaxLady.com" className="text-secondary hover:underline">Diane@TinyTaxLady.com</a></p>
                </div>
                <p className="pt-4">
                  We take your privacy seriously and will respond to all inquiries within a reasonable timeframe.
                </p>
              </CardContent>
            </Card>

            <div className="text-center pt-8">
              <p className="text-sm text-muted-foreground">
                Thank you for trusting Tiny Tax Lady with your tax preparation needs. Your privacy and security are our highest priorities.
              </p>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Privacy;
